import React from "react";
import { Partner } from "../../models/Partner";
import { getSchoolings } from "../../services/schooling.service.ts";
import Loader from "../Loader.tsx";
import { useFormik } from "formik";
import { EditProfileSchema } from "../../validation/schemas/EditProfile.ts";
import { FillEditProfile } from "../../interfaces/formik/IEditUser.ts";
import InputEditProfile from "../formik/InputEditProfile.tsx";
import SelectEditProfile from "../formik/SelectEditProfile.tsx";
import InputPhone from "../formik/InputPhone.tsx";
import TextAreaEditProfile from "../formik/TextAreaEditProfile.tsx";
import { useNavigate } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import { updatePartner } from "../../services/partner.service.ts";
import { getProceedingByPartner } from "../../services/proceedings.service.ts";
import { updateFileProceeding } from "../../services/files.service.ts";
import ModalContainer from "../ModalContainer.tsx";
import TooltipContent from "./TooltipContent.tsx";
import { getProfileTabPaneClass } from "./profileTabUtils.ts";

const InformationUser = ({
  partner,
  reloadPage,
  disabled = false,
  isTabActive = false,
  embedded = false,
}: {
  partner: Partner;
  reloadPage: () => void;
  disabled?: boolean;
  isTabActive?: boolean;
  embedded?: boolean;
}) => {
  const navigate = useNavigate();
  const [schoolings, setSchoolings] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const loadDependencies = async () => {
    try {
      const response = await getSchoolings();
      setSchoolings(response);
    } catch (error) {
      console.error(error);
    }
  };

  const maritalStatusList = [
    { id: "SOLTERO", name: "Soltero" },
    { id: "CASADO", name: "Casado" },
    { id: "DIVORCIADO LEGALMENTE", name: "Divorciado legalmente" },
    { id: "SEPARADO LEGALMENTE", name: "Separado Legalmente" },
    { id: "VIUDO", name: "Viudo" },
  ];

  const formik = useFormik({
    validationSchema: EditProfileSchema,
    initialValues: FillEditProfile(partner),
    onSubmit: async (values) => {
      const {
        clockNumber,
        originalAddress,
        originalAddressBeneficiary,
        address,
        addressBeneficiary,
        nss,
        ...dataToSend
      } = values;
      setLoading(true);
      try {
        await updatePartner(partner.person.id, {
          ...dataToSend,
          ...(nss ? { nss: String(nss) } : {}),
          address:
            originalAddress?.replace(/&nbsp;/g, " ") === address
              ? originalAddress
              : address,
          addressBeneficiary:
            originalAddressBeneficiary?.replace(/&nbsp;/g, " ") ===
            addressBeneficiary
              ? originalAddressBeneficiary
              : addressBeneficiary,
        });
        const proceeding = await getProceedingByPartner(partner.id);
        const proceedingRFC = proceeding.files.find((file) =>
          file.fields.map((field) => field.field).includes("RFC")
        );
        if (proceedingRFC) {
          const data = {
            proceedingId: proceeding.proceeding.id,
            fileTypeId: proceedingRFC.documentType.id,
            fileId: proceedingRFC.file.id,
            fields: [
              {
                field: "RFC",
                value: values.rfc,
              },
            ],
          };
          await updateFileProceeding(proceedingRFC.id, data);
        }

        notification("Éxito", "Usuario actualizado correctamente", "success");
      } catch (error) {
        console.log(error);
        notification(
          "Error",
          "Ocurrió un error al actualizar el usuario",
          "danger"
        );
      } finally {
        reloadPage();
        setLoading(false);
      }
    },
  });

  React.useEffect(() => {
    loadDependencies();
  }, []);

  const [idmexModal, setIdmexModal] = React.useState(false);
  const [ocrModal, setOcrModal] = React.useState(false);
  const [showContractTooltip, setShowContractTooltip] = React.useState(false);

  const getMissingFields = (person: any): string[] => {
    const excludedFields = [
      "tipoSangre",
      "tratamiento",
      "alergias",
      "enfermedad",
      "fromPhone",
      "id",
      "createdAt",
      "updateAt",
      "isBanned",
      "schooling",
      "idSchooling",
      "curp",
    ];

    const fieldLabels: { [key: string]: string } = {
      firstName: "Nombre",
      lastName: "Apellido Paterno",
      secondLastName: "Apellido Materno",
      email: "Correo electrónico",
      phone: "Teléfono",
      address: "Domicilio",
      birthdate: "Fecha de nacimiento",
      genere: "Género",
      maritalStatus: "Estado civil",
      rfc: "RFC",
      nss: "Número de Seguro Social",
      idMex: "IDMEX",
      ocr: "OCR",
      idSchooling: "Escolaridad",
    };

    const missingFields: string[] = [];

    for (let key in person) {
      if (!person.hasOwnProperty(key)) continue;
      if (excludedFields.includes(key)) continue;

      if (key === "nss") {
        const nssValue = person[key];
        if (!nssValue || nssValue === "" || nssValue === null) {
          missingFields.push(fieldLabels[key] || key);
        } else {
          const nssDigits = String(nssValue).replace(/\D/g, "");
          if (nssDigits.length !== 10 && nssDigits.length !== 11) {
            missingFields.push(`${fieldLabels[key] || key} (debe tener 10 u 11 dígitos)`);
          }
        }
        continue;
      }

      if (person[key] === "" || person[key] === null || person[key] === undefined) {
        if (fieldLabels[key]) {
          missingFields.push(fieldLabels[key]);
        }
      }
    }

    return missingFields;
  };

  const missingFields = React.useMemo(() => {
    return getMissingFields(partner?.person || {});
  }, [partner]);

  React.useEffect(() => {
    formik.setValues(FillEditProfile(partner));
    console.log("disabled", disabled);
  }, [partner]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.contract-tooltip')) {
        setShowContractTooltip(false);
      }
    };

    if (showContractTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showContractTooltip]);

  return (
    <div
      className={getProfileTabPaneClass(isTabActive, embedded)}
      id="informacion"
      style={{ padding: embedded ? 0 : "24px" }}
    >
      <style>{`
        #informacion .btn-link:hover {
          color: #50B7C0 !important;
          transform: scale(1.1);
        }
        #informacion .btn-link i {
          transition: all 0.2s ease;
        }
        #informacion .btn-link:hover i {
          color: #50B7C0 !important;
        }
        #informacion .contract-tooltip {
          position: relative;
          display: inline-block;
        }
        #informacion .contract-tooltip-content {
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 8px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          min-width: 300px;
          max-width: 400px;
        }
        #informacion .contract-tooltip-content::after {
          content: '';
          position: absolute;
          top: 100%;
          right: 20px;
          border: 8px solid transparent;
          border-top-color: #fff;
        }
      `}</style>
      <div style={{ position: "relative" }}>
        {missingFields.length > 0 && (
          <div 
            className="contract-tooltip" 
            style={{ 
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 10,
            }}
          >
            <button
              type="button"
              className="btn btn-sm"
              style={{
                background: missingFields.length > 0 ? "#fef3c7" : "#d1fae5",
                color: missingFields.length > 0 ? "#92400e" : "#065f46",
                border: `1px solid ${missingFields.length > 0 ? "#fde68a" : "#a7f3d0"}`,
                borderRadius: "6px",
                padding: "8px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
              onClick={() => setShowContractTooltip(!showContractTooltip)}
            >
              <i className={`fa-solid ${missingFields.length > 0 ? "fa-exclamation-triangle" : "fa-check-circle"}`}></i>
              <span>
                {missingFields.length > 0 
                  ? `Faltan ${missingFields.length} campo${missingFields.length > 1 ? 's' : ''} para contratos`
                  : "Información completa"
                }
              </span>
              <i className={`fa-solid fa-chevron-${showContractTooltip ? 'up' : 'down'}`} style={{ fontSize: "10px" }}></i>
            </button>
            {showContractTooltip && (
              <div className="contract-tooltip-content">
                <div style={{ marginBottom: "8px" }}>
                  <strong style={{ color: "#374151", fontSize: "14px" }}>
                    <i className="fa-solid fa-file-contract me-2" style={{ color: "#50B7C0" }}></i>
                    Campos requeridos para contratos:
                  </strong>
                </div>
                {missingFields.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: "20px", color: "#6b7280", fontSize: "13px" }}>
                    {missingFields.map((field, index) => (
                      <li key={index} style={{ marginBottom: "4px" }}>{field}</li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: "#059669", fontSize: "13px" }}>
                    <i className="fa-solid fa-check-circle me-2"></i>
                    Todos los campos requeridos están completos
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="table-responsive form-inline">
        <table className="table table-profile align-middle" style={{ marginBottom: 0 }}>
          <tbody>

            <InputEditProfile
              formik={formik}
              field="name"
              label="Nombre"
              placeholder="Nombre"
              sm={4}
              unclickable={disabled}
            />
            <InputEditProfile
              formik={formik}
              field="lastName"
              label="Apellido Paterno"
              placeholder="Apellido Paterno"
              sm={4}
              unclickable={disabled}
            />
            <InputEditProfile
              formik={formik}
              field="secondLastName"
              label="Apellido Materno"
              placeholder="Apellido Materno"
              sm={4}
              unclickable={disabled}
            />
            <InputEditProfile
              formik={formik}
              field="email"
              label="Correo electrónico"
              placeholder="Correo electrónico"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="genere"
              label="Género"
              placeholder="Género"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="rfc"
              label="RFC"
              placeholder="RFC"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="nss"
              label="Número de Seguro Social"
              placeholder="Número de Seguro Social"
              sm={4}
              type="text"
              numericOnly={true}
            />
            <InputEditProfile
              formik={formik}
              field="idMex"
              label="IDMEX"
              placeholder="IDMEX"
              sm={4}
              tooltip={
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIdmexModal(true);
                  }}
                  className="btn btn-sm btn-link p-0 ms-2"
                  style={{
                    color: "#6b7280",
                    textDecoration: "none",
                    padding: "2px 4px",
                    minWidth: "auto",
                    lineHeight: "1",
                  }}
                  title="Información sobre IDMEX"
                >
                  <i className="fa-solid fa-circle-question" style={{ fontSize: "14px" }}></i>
                </button>
              }
            />
            <InputEditProfile
              formik={formik}
              field="ocr"
              label="OCR"
              placeholder="OCR"
              sm={4}
              tooltip={
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setOcrModal(true);
                  }}
                  className="btn btn-sm btn-link p-0 ms-2"
                  style={{
                    color: "#6b7280",
                    textDecoration: "none",
                    padding: "2px 4px",
                    minWidth: "auto",
                    lineHeight: "1",
                  }}
                  title="Información sobre OCR"
                >
                  <i className="fa-solid fa-circle-question" style={{ fontSize: "14px" }}></i>
                </button>
              }
            />

            <tr>
              <td className="field" style={{ verticalAlign: "middle" }}>Estado civil : </td>
              <td>
                <SelectEditProfile
                  formik={formik}
                  field="maritalStatus"
                  list={maritalStatusList}
                  label="Estado Civil"
                  numeric={false}
                  sm={4}
                />
              </td>
            </tr>

            <InputPhone
              formik={formik}
              field="phone"
              label="Teléfono"
              placeholder="Teléfono"
              sm={4}
            />

            <tr>
              <td className="field" style={{ verticalAlign: "middle" }}>Escolaridad : </td>
              <td>
                <SelectEditProfile
                  formik={formik}
                  field="idSchooling"
                  list={schoolings}
                  label="Escolaridad"
                  sm={4}
                />
              </td>
            </tr>

            <TextAreaEditProfile
              formik={formik}
              field="address"
              label={
                <>
                  Domicilio : <br />
                  <small style={{ fontSize: "11px", fontWeight: "normal" }}>
                  (Calle, número, colonia, código postal)
                  </small>
                </>
              }
              placeholder="Ingrese su domicilio completo (Calle, número, colonia, código postal)"
              sm={4}
              rows={3}
            />
            <InputEditProfile
              formik={formik}
              field="birthdate"
              label="Fecha de nacimiento"
              placeholder="Fecha de nacimiento"
              type="date"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="alergias"
              label="Alergias"
              placeholder="Alergias"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="tipoSangre"
              label="Tipo de sangre"
              placeholder="Tipo de sangre"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="enfermedad"
              label="Enfermedad"
              placeholder="Enfermedad"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="tratamiento"
              label="Tratamiento"
              placeholder="Tratamiento"
              sm={4}
            />
            <tr>
              <td className="field" style={{ verticalAlign: "top", paddingTop: "12px" }}>&nbsp;</td>
              <td>
                <div className="col-md-8" style={{
                  background: "#f8f9fa",
                  borderLeft: "4px solid #50B7C0",
                  padding: "16px 20px",
                  marginTop: "0",
                  marginBottom: "16px",
                  borderRadius: "8px",
                }}>
                  <h5 style={{
                    margin: 0,
                    marginBottom: "8px",
                    color: "#374151",
                    fontSize: "15px",
                    fontWeight: "600",
                  }}>
                    <i className="fa-solid fa-user-shield me-2" style={{ color: "#50B7C0" }}></i>
                    Información del Beneficiario
                  </h5>
                  <p style={{
                    margin: 0,
                    color: "#6b7280",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}>
                    Para el caso de su fallecimiento o desaparición derivada de un acto delincuencial, 
                    en apego a lo establecido en los Artículos 25 Fracción X y 501 de LA LEY, 
                    EL TRABAJADOR designa como su beneficiario para el pago de los salarios y 
                    prestaciones devengadas y no cobradas, en primer lugar con derecho al 100%, a:
                  </p>
                </div>
              </td>
            </tr>
            <InputEditProfile
              formik={formik}
              field="firstNameBeneficiary"
              label="Nombre del Beneficiario"
              placeholder="Nombre del Beneficiario"
              sm={4}
              type="text"
            />
            <InputEditProfile
              formik={formik}
              field="lastNameBeneficiary"
              label="Apellido Paterno del Beneficiario"
              placeholder="Apellido Paterno del Beneficiario"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="secondLastNameBeneficiary"
              label="Apellido Materno del Beneficiario"
              placeholder="Apellido Materno del Beneficiario"
              sm={4}
            />
            <InputEditProfile
              formik={formik}
              field="relationshipBeneficiary"
              label="Parentesco del Beneficiario"
              placeholder="Parentesco del Beneficiario"
              sm={4}
            />
            <InputPhone
              formik={formik}
              field="phoneBeneficiary"
              label="Teléfono del Beneficiario"
              placeholder="Teléfono del Beneficiario"
              sm={4}
            />
            <TextAreaEditProfile
              formik={formik}
              field="addressBeneficiary"
              label="Domicilio del Beneficiario"
              placeholder="Ingrese el domicilio completo del beneficiario (Calle, número, colonia, código postal)"
              sm={4}
              rows={3}
            />
            <tr className="highlight">
              <td className="field">&nbsp;</td>
              <td className="">
                <div className="d-flex gap-2" style={{ marginTop: "32px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}>
                <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ minWidth: "140px" }}
                  onClick={() => navigate("/colaboradores")}
                >
                    <i className="fa-solid fa-arrow-left me-2"></i>
                  Regresar
                </button>
                <button
                  type="submit"
                    className="btn btn-azul"
                    style={{ minWidth: "140px" }}
                  onClick={() => formik.handleSubmit()}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Actualizando...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-floppy-disk me-2"></i>
                  Actualizar
                      </>
                    )}
                </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ModalContainer
        open={idmexModal}
        title="IDMEX"
        closeFn={() => setIdmexModal(false)}
        size="lg"
      >
        <TooltipContent type="IDMEX" />
      </ModalContainer>

      <ModalContainer
        open={ocrModal}
        title="OCR"
        closeFn={() => setOcrModal(false)}
        size="lg"
      >
        <TooltipContent type="OCR" />
      </ModalContainer>

      <Loader isLoading={loading} />
    </div>
  );
};

export default InformationUser;
